import React from "react";

import { css } from "@emotion/core";
import { partial, uniqBy } from "lodash";

import {
  Button,
  ButtonGroup,
  Colors,
  Margin,
  Text,
} from "@zapier/design-system";

import { Currency } from "@zapier/currency";
import PaymentMethodImageStrip from "@zapier/payment/esm/components/PaymentMethodImageStrip";
import { CurrencyRulesetItem } from "@zapier/payment/esm/hooks/useCurrencyRuleset";
import { PaymentSourceType } from "@zapier/payment/esm/types";

import PaymentMethodSelect from "../PaymentMethodSelect";

type Props = {
  /** Currently selected method */
  currentMethod: PaymentSourceType | null;
  /** Call to select a payment source */
  onSelectSource: (method: PaymentSourceType, hasSwitched: boolean) => void;
  /** The currently selected currency */
  selectedCurrency: Currency;
  /** Array of supported payment methods */
  supportedMethods: Array<CurrencyRulesetItem>;
};

const flexCss = css`
  display: flex;
  justify-content: center;
`;

const sectionCss = css`
  background-color: ${Colors.neutral200};
  border-radius: 3px;
  padding: 15px;
  text-align: center;

  @media screen and (min-width: 600px) {
    padding: 40px;
  }
`;

const selectCss = css`
  margin-top: 20px;
`;

const joinWithLast = (
  list: Array<string>,
  joiner: string,
  last: string
): string | undefined | null => {
  if (list.length < 2) {
    return null;
  }

  const fullList = list.join(joiner);
  const lastJoin = fullList.lastIndexOf(joiner);
  return (
    fullList.substring(0, lastJoin) +
    last +
    fullList.substring(lastJoin + joiner.length)
  );
};

const methodResolver: { [P in PaymentSourceType]: { addText: string } } = {
  [PaymentSourceType.card]: {
    addText: "Add card",
  },
  [PaymentSourceType.paypal]: {
    addText: "Connect PayPal",
  },
  [PaymentSourceType.bank]: {
    addText: "Add bank account",
  },
  [PaymentSourceType.manual]: {
    addText: "",
  },
};

const NoPaymentInformation: React.FC<Props> = ({
  currentMethod,
  onSelectSource,
  selectedCurrency,
  supportedMethods,
}) => {
  const acceptText = joinWithLast(
    supportedMethods.map(item => item.submethodDisplay),
    ", ",
    " or "
  );
  const uniqueMethods = uniqBy(supportedMethods, m => m.method);
  return (
    <section css={sectionCss}>
      <Text tag="h3" type="sectionHeader3">
        Add payment information
      </Text>
      {acceptText && (
        <Margin margin="20px 0">
          <Text type="paragraph3">
            We accept {acceptText} for payments in {selectedCurrency.name}{" "}
            {selectedCurrency.symbol}.
          </Text>
        </Margin>
      )}
      <div css={flexCss}>
        <PaymentMethodImageStrip methods={supportedMethods} />
      </div>
      <div css={selectCss}>
        {uniqueMethods.length < 3 ? (
          <ButtonGroup columnSize="minmax(100px, 200px)" justify="center">
            {uniqueMethods
              .map(m => m.method)
              .map(m => {
                return (
                  <Button
                    ariaLabel={methodResolver[m].addText}
                    color="secondary"
                    key={m}
                    onClick={partial(onSelectSource, m, false)}
                    size="small"
                  >
                    {methodResolver[m].addText}
                  </Button>
                );
              })}
          </ButtonGroup>
        ) : (
          uniqueMethods.length && (
            <div css={flexCss}>
              <PaymentMethodSelect
                currentMethod={currentMethod}
                onChange={onSelectSource}
                // @ts-ignore - types mismatch, uniqueMethods is some generated GQL type, expects SupportedPaymentMethod[]
                supportedMethods={uniqueMethods}
              />
            </div>
          )
        )}
      </div>
    </section>
  );
};

export default NoPaymentInformation;
